/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Input, OnInit, inject} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {finalize} from 'rxjs';
import * as _ from 'lodash';

import template from './site-list-page.component.html';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {AdUnitApiService, AdUnitModel} from '../ad-unit/ad-unit-api.service';
import {
  SignalAdminListColumn,
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {bulkApiErrorsFromResponse} from '../common/controller/bulk-action-errors';
import {ALL_COUNTRIES} from '../common/directives/country-select/countries';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';
import {SiteApiService, SiteModel} from './site-api.service';
import {SiteBulkActionService} from './site-bulk-action.service';
import {ADVERTISING_SHARED} from '../advertising/advertising-shared';

export function restoredSiteCountrySelection(allCountries: any[], selectIn: any): string[] {
  const availableCountries = _.uniq(allCountries.map((country) => country.country).filter(Boolean));
  if (availableCountries.length <= 1 || selectIn?.countrySelected === false) {
    return [];
  }
  const selectedCountries = Object.keys(selectIn?.country || {}).filter((country) => selectIn.country[country] === true);
  if (!selectedCountries.length || selectedCountries.length >= availableCountries.length) {
    return [];
  }
  return selectedCountries.every((country) => availableCountries.includes(country)) ? selectedCountries : [];
}

@Component({standalone: true, imports: [...ADVERTISING_SHARED], selector: 'adn-site-list-page', template})
export class SiteListPageComponent implements OnInit {
  readonly itemStateName = 'app.inv.sites.site';
  readonly adUnitItemStateName = 'app.inv.ad-units.ad-unit';
  readonly siteGroupItemStateName = 'app.inv.site-groups.site-group';
  readonly earningsAccountItemStateName = 'app.inv.earnings-accounts.earnings-account';
  readonly showMerchantColumn: boolean;
  readonly columns: SignalAdminListColumn[];
  readonly childColumns: SignalAdminListColumn[] = [
    {param: 'name'},
    {param: '', label: 'dimensions', sortable: false},
    {param: 'description'},
    {param: '', label: 'tagId', sortable: false}
  ];
  readonly allCountries = this.localNetworkInfo.getSiteCountries()
    .filter((country: any) => !!country.country)
    .map((country: any) => ({
      ...country,
      countryName: _.find(ALL_COUNTRIES, {code: country.country})?.name || country.country
    }));
  countryPopoverOpen = false;
  countryWorking: string[] = [];
  readonly list = inject(SignalAdminResourceListStateFactory).create<SiteModel>(
    inject(SiteApiService), {listId: 'fullSiteList', baseTable: 'site'}, inject(DestroyRef)
  );

  private readonly permissions = inject(LocalUserPermissions);
  private readonly localUserProfile = inject(LocalUserProfile);
  private readonly bulkActions = inject(SiteBulkActionService);
  private readonly modalLoading = inject(AdnModalLoadingService);
  private readonly factory = inject(SignalAdminResourceListStateFactory);
  private readonly adUnitApi = inject(AdUnitApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly children: Record<string, SignalAdminResourceListState<AdUnitModel>> = {};
  private readonly merchantsById: Record<string, any> = {};

  constructor(private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()) {
    const activeMerchants = _.filter(this.localNetworkInfo.getInfo('productFeedProductCounts') || [], (feed: any) => feed.productCount > 0);
    this.showMerchantColumn = activeMerchants.length > 1;
    activeMerchants.forEach((merchant: any) => this.merchantsById[merchant.id] = merchant);
    this.columns = [
      {param: 'name'},
      {param: 'siteUrl'},
      {param: 'country'},
      ...(this.showMerchantColumn ? [{param: '', labelInstant: 'Merchant', sortable: false}] : []),
      {label: 'siteGroup', param: 'SiteGroup'},
      {label: 'earningsAccount', param: 'EarningsAccount'},
      {label: 'status', param: 'requestedInLastDay'}
    ];
  }

  @Input() set readOnly(value: boolean) { this.list.readOnly.set(value); }

  ngOnInit(): void {
    const selectIn = _.get(this.localUserProfile.getProfile(), ['adnLists', 'fullSiteList', 'selectIn']);
    this.countryWorking = restoredSiteCountrySelection(this.allCountries, selectIn);
    if (this.countryWorking.length) {
      this.list.setAdditionalParams(this.countryFilterParams());
    } else {
      this.list.load();
    }
  }

  countryName(site: SiteModel): string {
    return _.find(ALL_COUNTRIES, {code: site.country})?.name || site.country || '';
  }

  merchantSummary(site: SiteModel): string {
    const ids = (site.retailMerchantIds || []).map((id: any) => String(id));
    return ids.length ? ids.map((id: string) => this.merchantsById[id]?.uiTitle || this.merchantsById[id]?.title || id).join(', ') : 'All';
  }

  selectedCountryNames(): string[] {
    return this.countryWorking.map((code) => _.find(this.allCountries, {country: code})?.countryName || code);
  }

  toggleCountryPopover(): void { this.countryPopoverOpen = !this.countryPopoverOpen; }
  isCountrySelected(code: string): boolean { return this.countryWorking.includes(code); }

  toggleCountry(code: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.countryWorking = checked
      ? _.uniq([...this.countryWorking, code])
      : this.countryWorking.filter((selected) => selected !== code);
  }

  applyCountryFilter(): void {
    this.list.setAdditionalParams(this.countryFilterParams());
    this.saveCountryFilter();
    this.countryPopoverOpen = false;
  }

  clearCountryFilter(): void {
    this.countryWorking = [];
    this.list.setAdditionalParams({});
    this.saveCountryFilter();
    this.countryPopoverOpen = false;
  }

  private countryFilterParams(): Record<string, any> {
    const where = this.countryWorking.length && this.countryWorking.length < this.allCountries.length
      ? 'site.country in ' + this.countryWorking.join(',')
      : undefined;
    return where ? {where} : {};
  }

  private saveCountryFilter(): void {
    const selected = new Set(this.countryWorking);
    const applied = selected.size > 0 && selected.size < this.allCountries.length;
    const profile = this.localUserProfile.getProfile() || {};
    const selectIn = _.get(profile, ['adnLists', 'fullSiteList', 'selectIn'], {});
    this.localUserProfile.set('profile.adnLists.fullSiteList.selectIn', {
      ...selectIn,
      country: Object.fromEntries(this.allCountries.map((country) => [country.country, selected.has(country.country)])),
      countrySum: selected.size,
      countrySelected: applied
    });
  }

  openBulkActions(): void {
    const adapter: any = {
      bulkSelection: this.list.selected(),
      miscBulkAction: (modal: any, modalController: any, items: Partial<SiteModel>[]) => {
        if (!items.length) {
          modal.close();
          return;
        }
        this.modalLoading.applyModalLoading();
        this.list.saveMiscBulkAction(items).pipe(
          finalize(() => this.modalLoading.removeModalLoading()),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe({
          next: () => {
            modal.close({items});
          },
          error: (response) => {
            modalController.form = modalController.form || {};
            modalController.form.apiErrors = bulkApiErrorsFromResponse(response);
            modalController.form.bulkError = true;
          }
        });
      }
    };
    this.bulkActions.createBulkAction(adapter)();
  }

  earningsAccountLinkDisabled(): boolean { return !this.permissions.hasNetwork('MANAGE_BUSINESS'); }
  siteGroupLinkDisabled(): boolean { return !this.permissions.hasNetwork('MANAGE_SYSTEM'); }

  toggleChildren(site: SiteModel): void {
    site.showChildren = !site.showChildren;
    if (!this.children[site.id]) {
      const childList = this.factory.create(this.adUnitApi, {
        listId: 'fullAdUnitList', baseTable: 'adUnit', syncPageToUrl: false, additionalParams: {site: site.id}
      }, this.destroyRef);
      childList.readOnly.set(true);
      this.children[site.id] = childList;
      childList.load();
    }
  }

  childList(id: string): SignalAdminResourceListState<AdUnitModel> | undefined { return this.children[id]; }
  copyTagId(tagId: string): void { navigator.clipboard?.writeText(tagId); }
}
